import { BookData } from '../interfaces/book-data.interface';
import { CollageData } from '../interfaces/collage-data.interface';
import { DrawableData, isDrawableGroup } from '../interfaces/drawable-data.interface';

/**
 * Every person whose NAME this book prints — the set a "names are filled" gate has to judge.
 *
 * The album owner may or may not be in the result, and the caller must not depend on either: an owner
 * group is RESOLVED by `peopleType`, but the seating writer stamps `params.personId` on it all the same
 * (`assignPersonToGroupByDrawable`), so a promoted owner frame does carry a binding while an
 * untouched one does not. The caller already knows the owner from the album row and adds it itself.
 * What this walk adds is the rest — a class collage inside a student's book prints its classmates'
 * names too, so a named owner is no proof that the layout has no blank captions.
 *
 * Names are injected on every page, not only VIGNETTE spreads (the cover and FREE spreads run
 * names-only), so the walk covers the whole book rather than filtering by spread type.
 */
function collectInDrawables(drawables: readonly DrawableData[] | undefined, sink: Set<string>): void {
  for (const drawable of drawables ?? []) {
    // A hidden or non-rendered group never reaches paper, so its person cannot leave a blank caption.
    if (drawable.visible === false || drawable.skipRender === true) continue;
    if (!isDrawableGroup(drawable)) continue;

    const personId = (drawable.params as { personId?: string | null } | undefined)?.personId;
    if (personId != null) sink.add(personId);

    // Person groups nest, so a single-level walk under-reports (same reason the empty-frame gate recurses).
    collectInDrawables(drawable.drawableObjects, sink);
  }
}

function collectInCollages(collages: readonly CollageData[] | undefined, sink: Set<string>): void {
  for (const collage of collages ?? []) collectInDrawables(collage.drawableObjects, sink);
}

/**
 * Ids in walk order, deduplicated. A missing book is NOT "prints nobody": a caller that could not read
 * the model must treat that as its own fail-closed case instead of reading an empty list out of here.
 */
export function collectPrintedPersonIds(book: Pick<BookData, 'cover' | 'spreads'>): string[] {
  const sink = new Set<string>();
  collectInCollages(book.cover?.collages, sink);
  for (const spread of book.spreads ?? []) collectInCollages(spread?.collages, sink);
  return [...sink];
}
